import type { TFunction } from 'i18next';
import styles from './SimilarNameNudge.module.css';

interface ISimilarNameNudgeProps {
  /** The known spelling the typed name closely matches — null hides the nudge. */
  closeMatch: string | null;
  /** Shown only once the field/list is left — a nudge mid-typing is flicker. */
  visible: boolean;
  /** The already-translated warning sentence — each form words its own. */
  message: string;
  onUse: (name: string) => void;
  t: TFunction;
}

/**
 * One-line, non-blocking hint under a Material field when the typed
 * name is a separator/typo variant of a known one (fuzzy-matching design
 * 2026-08-31). A near-miss name silently severs the delivery↔stock fold-link
 * (lead-time learning, arrived amounts), so the known spelling is one tap away
 * — but free text stays first-class: nothing blocks the save.
 */
export function SimilarNameNudge({ closeMatch, visible, message, onUse, t }: ISimilarNameNudgeProps) {
  return (
    <FieldNotice
      visible={visible && closeMatch != null}
      message={message}
      actionLabel={t('logistics.similarName.use', { name: closeMatch ?? '' })}
      onAction={() => closeMatch != null && onUse(closeMatch)}
    />
  );
}

/** The under-field notice line both forms speak in — one sentence plus one
 *  underlined action. Two tones: `warn` (amber) for the similar-name
 *  SUGGESTION, `error` (red) for the hard one-record/one-order BLOCKS. */
interface IFieldNoticeProps {
  visible: boolean;
  message: string;
  actionLabel: string;
  onAction: () => void;
  tone?: 'warn' | 'error';
}

export function FieldNotice({ visible, message, actionLabel, onAction, tone = 'warn' }: IFieldNoticeProps) {
  if (!visible) return null;
  return (
    <div className={styles.nudge} data-tone={tone}>
      <span aria-hidden="true">⚠</span>
      <span>{message}</span>
      <button type="button" className={styles.nudgeBtn} onClick={onAction}>
        {actionLabel}
      </button>
    </div>
  );
}

/** The dropdown's green close-match chip — one render for both forms. */
export function CloseMatchChip({ show, t }: { show: boolean; t: TFunction }) {
  if (!show) return null;
  return <span className={styles.comboChip}>{t('logistics.similarName.chipClose')}</span>;
}
